import {
  Component,
  computed,
  effect,
  inject,
  model,
  signal,
  untracked,
  ViewChild,
  ElementRef,
  AfterViewInit,
  AfterViewChecked,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { FormsModule } from '@angular/forms';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SearchType } from '../../../enums/search-type';
import { AnswerSearch } from '../../../models/recent-search';
import { TextareaSearchComponent } from '../textarea-search/textarea-search.component';
import { OpenSearchService } from '../../../services/open-search.service';
import { MarkdownToPlainTextPipe } from '../../../pipes/markdown-to-plain-text.pipe';
import { OpenQueryInteraction, OptionTypeResponse } from '../../../models/response-open-search';
import { TypeResponse } from '../../../enums/type-response';
import { LinkDocumentsPipe } from '../../../pipes/link-documents.pipe';
import { SadpathComponent } from '../sadpath/sadpath.component';
import { TypeSadpath } from '../../../enums/type-sadpath';
import { TranslatableField } from '../../../models/TranslatableField';
import { CopyDataDirective } from '../../../directives/copy-data.directive';
import { UserService } from '../../../services/user.service';
import { OpenSearchDrawerService } from '../../../services/open-search-drawer.service';
import { StepOpenSearchResponseView } from '../../../constants/steps-boarding.contasts';
import { OnBoardingService } from '../../../services/on-boarding.service';
import { NewSearchComponent } from './new-search/new-search.component';
import { DrawerSearchPanelService } from '../../../services/drawer-search-panel.service';
import {
  CuratedLesson,
  CuratedLessonSource,
  SourceResponse,
} from '../../../models/response-open-search';
import { TextHighlightService } from '../../../services/text-highlight.service';
import { ScrollToTextPayload } from '../../../models/ScrollToTextPayload';
import { ErrorNotFound404Component } from '../../error-not-found-404/error-not-found-404.component';
import { ExportService } from '../../../services/export.service';
import { MatMenuModule } from '@angular/material/menu';

@Component({
  selector: 'app-response-view',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    MatIconModule,
    MatButtonModule,
    FormsModule,
    MatDividerModule,
    MatTooltipModule,
    MatSelectModule,
    TextareaSearchComponent,
    MarkdownToPlainTextPipe,
    SadpathComponent,
    CopyDataDirective,
    NewSearchComponent,
    ErrorNotFound404Component,
    MatMenuModule,
  ],
  providers: [LinkDocumentsPipe],
  templateUrl: './response-view.component.html',
  styleUrls: ['./response-view.component.scss'],
})
export class ResponseViewComponent implements AfterViewInit, AfterViewChecked, OnDestroy {
  onBoardingService = inject(OnBoardingService);
  private openSearchService = inject(OpenSearchService);
  private exportService = inject(ExportService);
  private translateService = inject(TranslateService);
  private sourcesDrawer = inject(OpenSearchDrawerService);
  private searchPanelDrawer = inject(DrawerSearchPanelService);
  private textHighlightService = inject(TextHighlightService);
  drawer = inject(OpenSearchDrawerService);
  userService = inject(UserService);
  TypeResponse = TypeResponse;
  noLengthSadPath = TypeSadpath.LENGTH;
  noTextSadPath = TypeSadpath.TEXT;
  private readonly onboardingKey = 'view_-response_opensearch';
  user = computed(() => this.userService.user());
  isShowAllResults = model<boolean>(false);
  showSearchPanel = model<boolean>();
  showAllResults = model<boolean>(true);
  items = model<AnswerSearch[]>([]);
  showResponseView = model<boolean>(true);
  activeDestroyComponent = model<boolean>(false);
  inputText = model<TranslatableField | null>(null);
  searchType = model<SearchType>();
  queryMode = model<'default' | 'curated-lessons'>('default');
  quick = signal(SearchType.QUICK);
  recent = signal(SearchType.RECENT);
  isComponentResponseView = model<boolean>(true);
  historyResponse = computed(() => this.openSearchService.historyResponse());
  errorDrawer = computed(() => this.openSearchService.errorDrawer());
  spinnerConversational = computed(() => this.openSearchService.spinnerConversational());
  options: OptionTypeResponse[] = [];
  rightWidth = 360;
  showOnlyNewSearch = model<boolean>(false);
  promptText = computed(() => this.openSearchService.inputText());
  thumbStates = signal<Record<string, 'up' | 'down' | null>>({});
  expandedQuestions = signal<Record<string, boolean>>({});
  newSearchSignal = computed(() => this.openSearchService.isNewSearch());
  @ViewChild('scrollContainer') scrollContainerRef?: ElementRef<HTMLElement>;
  @ViewChild('stickyHeaderRef') stickyHeaderRef?: ElementRef<HTMLElement>;
  currentStickyHeader = signal<{ answer: string; index: number } | null>(null);
  stickyHeader = computed(() =>
    this.openSearchService.historyResponse()?.openQueryInteractions.find(x => x.isSticky),
  );
  private lastAutoOpenedAnswerId: string | null = null;
  private lastConversationId: string | null = null;
  private scrollListener?: () => void;
  private lastScrollKey = '';
  private pendingScrollIndex = -1;
  private lastHistoryLength = 0;
  private activeSearchQuery = signal<string | null>(null);
  private activeSearchInteractionIndex = signal<number>(-1);
  private lastActiveStickyIndex = -1;
  private pinnedActiveIndex = -1;
  private pinnedActiveTimer: ReturnType<typeof setTimeout> | null = null;
  private lastCuratedLessonsResetKey = '';

  hasAnySources = computed(() => {
    const interactions = this.historyResponse()?.openQueryInteractions ?? [];
    return interactions.some(i => i.response !== null && this.hasSourcesForInteraction(i));
  });

  curatedLessons = computed<CuratedLesson[]>(() => {
    const history = this.historyResponse();
    return history?.modeType?.toLowerCase() === 'lessons'
      ? (history.modeResponse?.lessons ?? [])
      : [];
  });
  isCuratedInteraction(interaction: OpenQueryInteraction): boolean {
    return interaction.modeType?.toLowerCase() === 'lessons';
  }

  curatedLessonsForInteraction(interaction: OpenQueryInteraction): CuratedLesson[] {
    if (!this.isCuratedInteraction(interaction)) return [];
    return interaction.modeResponse?.lessons ?? [];
  }

  hasSourcesForInteraction(interaction: OpenQueryInteraction): boolean {
    if ((interaction.sources?.length ?? 0) > 0) return true;
    return (
      this.isCuratedInteraction(interaction) &&
      this.curatedLessonsForInteraction(interaction).some(
        lesson => this.normalizeLessonSources(lesson).length > 0,
      )
    );
  }

  normalizedCuratedLessonsForInteraction(interaction: OpenQueryInteraction) {
    let reference = 0;
    return this.curatedLessonsForInteraction(interaction).map(lesson => ({
      lesson,
      sources: this.normalizeLessonSources(lesson).map(source => ({
        ...source,
        reference: ++reference,
      })),
    }));
  }
  normalizedCuratedLessons = computed(() => {
    let reference = 0;
    return this.curatedLessons().map(lesson => ({
      lesson,
      sources: this.normalizeLessonSources(lesson).map(source => ({
        ...source,
        reference: ++reference,
      })),
    }));
  });
  isCuratedLessonsMode = computed(() => {
    const history = this.historyResponse();
    const lastInteraction = history?.openQueryInteractions.at(-1);
    return history
      ? !!lastInteraction && this.isCuratedInteraction(lastInteraction)
      : this.queryMode() === 'curated-lessons';
  });
  showAllCuratedLessons = signal(false);
  visibleCuratedLessons = computed(() =>
    this.showAllCuratedLessons() ? this.curatedLessons() : this.curatedLessons().slice(0, 1),
  );
  visibleNormalizedCuratedLessons = computed(() =>
    this.showAllCuratedLessons()
      ? this.normalizedCuratedLessons()
      : this.normalizedCuratedLessons().slice(0, 1),
  );

  toggleCuratedLessons(): void {
    this.showAllCuratedLessons.update(showAll => !showAll);
  }

  lessonValue(lesson: CuratedLesson, keys: string[]): string {
    for (const key of keys) {
      const value = lesson[key];
      if (typeof value === 'string' && value.trim()) return value;
    }
    return '';
  }

  lessonText(lesson: CuratedLesson): string {
    return this.lessonValue(lesson, ['lesson', 'description']);
  }

  lessonOperationCodes(lesson: CuratedLesson): string[] {
    const value = lesson.operation_code ?? lesson.operationCode ?? lesson.operationCodes;
    return this.toStringArray(value);
  }

  lessonParts(lesson: CuratedLesson): { finding: string; recommendation: string } | null {
    const structuredFinding = this.lessonValue(lesson, ['finding', 'hallazgo']);
    const structuredRecommendation = this.lessonValue(lesson, ['recommendation', 'recomendacion']);
    if (structuredFinding || structuredRecommendation) {
      return { finding: structuredFinding, recommendation: structuredRecommendation };
    }

    const text = this.lessonText(lesson);
    if (!text) return null;
    const findingMatch =
      /\b(?:hallazgo|finding|achado|constata(?:ção|cion|ción|t)|constat)\s*:\s*/i.exec(text);
    const recommendationMatch =
      /\b(?:recomendaci(?:ón|on)|recommendation|recomenda(?:ção|cion|ción)|recommandation)\s*:\s*/i.exec(
        text,
      );
    if (!findingMatch && !recommendationMatch) return null;

    const findingStart = findingMatch ? findingMatch.index + findingMatch[0].length : -1;
    const recommendationStart = recommendationMatch
      ? recommendationMatch.index + recommendationMatch[0].length
      : -1;
    return {
      finding:
        findingStart >= 0
          ? text.slice(findingStart, recommendationMatch?.index ?? text.length).trim()
          : '',
      recommendation: recommendationStart >= 0 ? text.slice(recommendationStart).trim() : '',
    };
  }

  private normalizeLessonSources(lesson: CuratedLesson): Omit<CuratedLessonSource, 'reference'>[] {
    const urls = this.toStringArray(lesson.url);
    const fileNames = this.toStringArray(lesson.file_name ?? lesson.fileName);
    const nested = Array.isArray(lesson.sources) ? lesson.sources : [];
    const nestedSources = nested.flatMap(source => {
      const value = source as SourceResponse & { fileName?: string; filename?: string };
      return value.url
        ? [
            {
              url: value.url,
              fileName: value.fileName ?? value.filename ?? value.description ?? value.url,
            },
          ]
        : [];
    });
    const pairedSources = urls.map((url, index) => ({
      url,
      fileName: fileNames[index] ?? url,
    }));
    return [...pairedSources, ...nestedSources].filter(source => /^https?:\/\//i.test(source.url));
  }

  private toStringArray(value: unknown): string[] {
    if (Array.isArray(value))
      return value.filter((item): item is string => typeof item === 'string');
    return typeof value === 'string' && value.trim() ? [value] : [];
  }

  setLessonSourceHover(url: string): void {
    this.openSearchService.setActiveTooltip({ url, tooltipClass: 'tooltip-no-wrap' });
  }

  clearLessonSourceHover(): void {
    this.openSearchService.clearActiveTooltip();
  }

  constructor() {
    this.userService.fetchUser();
    this.effectAddOptionsByResponse();
    this.effectSearchType();
    this.startedOnBoarding();
    this.effectAutoOpenDrawerOnResponse();

    effect(() => {
      const history = this.historyResponse();
      const mode = this.queryMode();
      const lessonKey = (history?.modeResponse?.lessons ?? [])
        .map(
          lesson =>
            `${this.lessonValue(lesson, ['title', 'lessonName', 'name'])}|${this.toStringArray(lesson.url).join(',')}`,
        )
        .join('||');
      const resetKey = `${history?.id ?? ''}|${history?.modeType ?? ''}|${mode}|${lessonKey}`;
      if (resetKey === this.lastCuratedLessonsResetKey) return;
      this.lastCuratedLessonsResetKey = resetKey;
      this.showAllCuratedLessons.set(false);
    });

    effect(() => {
      const history = this.historyResponse();
      const interactions = history?.openQueryInteractions || [];
      const currentId = history?.id ?? null;
      const isNew = currentId !== this.lastConversationId;
      if (isNew) {
        this.thumbStates.set({});
        this.lastConversationId = currentId;
        this.lastHistoryLength = interactions.length;
      } else if (interactions.length > this.lastHistoryLength) {
        this.lastHistoryLength = interactions.length;
      }

      if (interactions.length > 0) {
        this.inputText.set(null);
        this.activeSearchQuery.set(null);
        this.activeSearchInteractionIndex.set(-1);
      }
    });

    effect(() => {
      const sticky = this.currentStickyHeader();
      const query = this.activeSearchQuery();
      const targetIndex = this.activeSearchInteractionIndex();
      if (!query || targetIndex < 0) return;
      setTimeout(() => {
        if (sticky && sticky.index === targetIndex) {
          const stickyEl = this.stickyHeaderRef?.nativeElement;
          if (stickyEl) {
            this.textHighlightService.clearHighlights(stickyEl);
            const stickyQuestionEl = stickyEl.querySelector<HTMLElement>('.question');
            if (stickyQuestionEl) this.applyHighlightOnly(stickyQuestionEl, query);
          }
        }
        const scrollContainer = this.scrollContainerRef?.nativeElement;
        if (scrollContainer) {
          const headers = Array.from(scrollContainer.querySelectorAll<HTMLElement>('.top-header'));
          const inlineHeader = headers[targetIndex];
          if (inlineHeader) {
            const questionEl = inlineHeader.querySelector<HTMLElement>('.question');
            if (questionEl) {
              this.textHighlightService.clearHighlights(questionEl);
              this.applyHighlightOnly(questionEl, query);
            }
          }
        }
      }, 0);
    });

    effect(() => {
      if (this.newSearchSignal()) this.drawer.open(this.options, this.rightWidth);
    });

    effect(() => {
      const isLoading = this.spinnerConversational();
      if (!isLoading) return;
      setTimeout(() => {
        const c = this.scrollContainerRef?.nativeElement;
        if (c) c.scrollTo({ top: c.scrollHeight, behavior: 'smooth' });
      }, 40);
    });

    effect(() => {
      const history = this.historyResponse()?.openQueryInteractions || [];
      if (history.length > this.lastHistoryLength) {
        this.inputText.set(null);
        this.lastHistoryLength = history.length;
        this.activeSearchQuery.set(null);
        this.activeSearchInteractionIndex.set(-1);
      }
    });

    effect(() => {
      const data = this.historyResponse();
      if (!data?.openQueryInteractions?.length) return;
      const interactions = data.openQueryInteractions;
      let lastAnsweredIndex = -1;
      for (let i = interactions.length - 1; i >= 0; i--) {
        if (interactions[i].response != null) {
          lastAnsweredIndex = i;
          break;
        }
      }
      if (lastAnsweredIndex < 0) return;
      const id = data.id ?? '';
      const qId = interactions[lastAnsweredIndex].question_id ?? String(lastAnsweredIndex);
      const scrollKey = `${id}|${qId}|${interactions.length}`;
      const currentKey = untracked(() => this.lastScrollKey);
      if (scrollKey === currentKey) return;
      untracked(() => {
        this.pendingScrollIndex = lastAnsweredIndex;
      });
    });

    effect(() => {
      const hasSources = this.hasAnySources();
      if (!hasSources) {
        this.sourcesDrawer.close();
      }
    });
  }

  ngAfterViewInit(): void {
    this.setupStickyHeaderDetection();
    localStorage.setItem('responseView', 'response');
  }

  ngAfterViewChecked(): void {
    if (this.pendingScrollIndex < 0) return;
    const scrollContainer = this.scrollContainerRef?.nativeElement;
    if (!scrollContainer) return;
    const headers = Array.from(scrollContainer.querySelectorAll('.top-header')) as HTMLElement[];
    if (!headers.length || headers.length <= this.pendingScrollIndex) return;
    const indexToScroll = this.pendingScrollIndex;
    this.pendingScrollIndex = -1;
    const data = this.historyResponse();
    if (data) {
      const interactions = data.openQueryInteractions;
      const qId = interactions[indexToScroll]?.question_id ?? String(indexToScroll);
      this.lastScrollKey = `${data.id ?? ''}|${qId}|${interactions.length}`;
    }
    this.scrollToInteractionAndHighlight(indexToScroll);
  }

  ngOnDestroy(): void {
    if (this.pinnedActiveTimer !== null) clearTimeout(this.pinnedActiveTimer);
    if (this.scrollContainerRef?.nativeElement && this.scrollListener) {
      this.scrollContainerRef.nativeElement.removeEventListener('scroll', this.scrollListener);
    }
  }

  exportResponseToExcel(interactionIndex: number): void {
    const interactions = this.historyResponse()?.openQueryInteractions;
    if (!interactions || interactionIndex < 0 || interactionIndex >= interactions.length) return;

    const interaction = interactions[interactionIndex];
    const markdown = interaction.response ?? '';
    if (!markdown) return;

    this.exportService.exportResponseToExcel(
      interactionIndex,
      markdown,
      interaction.sources,
      this.curatedLessonsForInteraction(interaction),
    );
  }

  exportResponseToDoc(interactionIndex: number): void {
    const interactions = this.historyResponse()?.openQueryInteractions;
    if (!interactions || interactionIndex < 0 || interactionIndex >= interactions.length) return;

    const markdown = interactions[interactionIndex].response ?? '';
    if (!markdown) return;

    this.exportService.exportResponseToDoc(
      interactionIndex,
      markdown,
      this.curatedLessonsForInteraction(interactions[interactionIndex]),
    );
  }

  scrollToExactText(payload: ScrollToTextPayload): void {
    const { interactionIndex, searchQuery, searchInLessons } = payload;
    const scrollContainer = this.scrollContainerRef?.nativeElement;
    if (!scrollContainer) return;
    this.textHighlightService.clearHighlights(scrollContainer);
    const stickyHeaderEl = this.stickyHeaderRef?.nativeElement;
    if (stickyHeaderEl) this.textHighlightService.clearHighlights(stickyHeaderEl);
    this.activeSearchQuery.set(searchQuery);
    this.activeSearchInteractionIndex.set(interactionIndex);
    const data = this.openSearchService._historyResponse();
    if (!data?.openQueryInteractions) return;
    const interactions = data.openQueryInteractions;
    if (interactionIndex < 0 || interactionIndex >= interactions.length) return;
    if (searchInLessons) this.showAllCuratedLessons.set(true);
    const expandCuratedResponse = this.isCuratedInteraction(interactions[interactionIndex]);
    interactions.forEach((item, i) => {
      item.isActive = i === interactionIndex;
      item.isSticky = false;
    });
    this.openSearchService._historyResponse.set({ ...data });
    this.currentStickyHeader.set(null);
    this.lastActiveStickyIndex = -1;
    this.pinActiveIndex(interactionIndex);
    const headers = Array.from(scrollContainer.querySelectorAll<HTMLElement>('.top-header'));
    const targetHeader = headers[interactionIndex];
    const doHighlight = () => {
      const questionId = interactions[interactionIndex].question_id;
      const responseEl = questionId
        ? scrollContainer.querySelector<HTMLElement>(`#response-${questionId}`)
        : (scrollContainer.querySelectorAll<HTMLElement>('.container-resize')[interactionIndex] ??
          null);
      if (responseEl) {
        if (expandCuratedResponse) {
          responseEl
            .querySelectorAll<HTMLElement>('.curated-project-list')
            .forEach(list => list.classList.add('curated-project-list--expanded'));
        }
        const found = this.textHighlightService.highlightAndScroll(
          responseEl,
          searchQuery,
          scrollContainer,
        );
        if (!found) {
          const allContainers =
            scrollContainer.querySelectorAll<HTMLElement>('.container-responde');
          const targetContainer = allContainers[interactionIndex];
          if (targetContainer)
            this.textHighlightService.highlightAndScroll(
              targetContainer,
              searchQuery,
              scrollContainer,
            );
        }
      }

      const inlineHeader = headers[interactionIndex];
      if (inlineHeader) {
        const questionEl = inlineHeader.querySelector<HTMLElement>('.question');
        if (questionEl) {
          this.textHighlightService.clearHighlights(questionEl);
          this.applyHighlightOnly(questionEl, searchQuery);
        }
      }

      const currentSticky = this.currentStickyHeader();
      if (currentSticky && currentSticky.index === interactionIndex) {
        const stickyEl = this.stickyHeaderRef?.nativeElement;
        if (stickyEl) {
          this.textHighlightService.clearHighlights(stickyEl);
          const stickyQuestionEl = stickyEl.querySelector<HTMLElement>('.question');
          if (stickyQuestionEl) this.applyHighlightOnly(stickyQuestionEl, searchQuery);
        }
      }
    };

    if (targetHeader) {
      const containerTop = scrollContainer.getBoundingClientRect().top;
      const headerTop = targetHeader.getBoundingClientRect().top;
      const targetScrollTop = scrollContainer.scrollTop + (headerTop - containerTop) - 16;
      scrollContainer.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
      setTimeout(doHighlight, searchInLessons ? 450 : 350);
    } else {
      doHighlight();
    }
  }

  effectAddOptionsByResponse(): void {
    effect(() => {
      this.options = [];
      if (this.isOperationalsHasContent()) {
        this.options.push({
          value: TypeResponse.OPERATIONALS,
          label: 'OPEN_SEARCH.OPERATIONAL',
          icon: 'link',
          className: 'option-operational',
        });
      }
      if (this.isPublicationsHasContent()) {
        this.options.push({
          value: TypeResponse.PUBLICATIONS,
          label: 'OPEN_SEARCH.LITERATURE',
          icon: 'link',
          className: 'option-literature',
        });
      }
    });
  }

  effectSearchType(): void {
    effect(() => {
      if (this.searchType() === this.quick() || this.searchType() === this.recent()) {
        this.searchType.set(this.searchType() ?? SearchType.DEFAULT);
        this.setItems();
      }
    });
  }

  setItems(): void {
    if (this.searchType() === this.recent()) {
      const items = JSON.parse(localStorage.getItem('recentSearchItems') || '[]');
      this.items.set(items);
    }
  }

  isPublicationsHasContent = computed(() => {
    const data = this.historyResponse()?.openQueryInteractions.find(x => x.response);
    if (!data?.response) return false;
    return data.sources.length > 0 && data.response !== '';
  });

  isOperationalsHasContent = computed(() => {
    const data = this.historyResponse()?.openQueryInteractions.find(x => x.response);
    if (!data?.response) return false;
    return data.sources.length > 0 && data.response !== '';
  });

  isNoLength = computed(() => {
    const data = this.historyResponse()?.openQueryInteractions.find(x => x.response);
    if (!data?.response) return false;
    return data.sources.length === 0;
  });

  isNoText = computed(() => {
    const data = this.historyResponse()?.openQueryInteractions.find(x => x.response);
    if (!data?.response) return false;
    return data.response === '';
  });

  mapCopySourcesPublicationsSummary = computed(() => {
    const data = this.historyResponse()?.openQueryInteractions.find(x => x.response);
    return data?.response ? this.mapSourcesCopy(data.sources ?? []) : [];
  });

  mapCopySourcesOperationalsSummary = computed(() => {
    const data = this.historyResponse()?.openQueryInteractions.find(x => x.response);
    return data?.response ? this.mapSourcesCopy(data.sources) : [];
  });

  mapCopyDocumentsPublicationsSummary = computed(() => {
    const data = this.historyResponse()?.openQueryInteractions.find(x => x.response);
    return data?.response ? this.mapDocumentsCopy(data.sources) : [];
  });

  mapCopyDocumentsOperationalsSummary = computed(() => {
    const data = this.historyResponse()?.openQueryInteractions.find(x => x.response);
    return data?.response ? this.mapDocumentsCopy(data.sources) : [];
  });

  mapCopyCuratedLessonsSummary = computed(() =>
    this.curatedLessons()
      .map(lesson => {
        const title = this.lessonValue(lesson, ['title', 'lessonName', 'name']);
        const text = this.lessonText(lesson);
        const finding = this.lessonValue(lesson, ['finding', 'hallazgo']);
        const recommendation = this.lessonValue(lesson, ['recommendation', 'recomendacion']);
        return [title, text || finding, !text && recommendation].filter(Boolean).join('\n');
      })
      .filter(Boolean)
      .join('\n\n'),
  );

  mapCopyCuratedLessonSourcesSummary = computed(() =>
    this.normalizedCuratedLessons()
      .flatMap(item => item.sources.map(source => `${source.fileName} - ${source.url}`))
      .join('\n'),
  );

  mapDocumentsCopy(documents: SourceResponse[]): string {
    return documents
      .map(
        doc =>
          this.translateService.instant('KNOWLEDGETOOLTIPS.PAGE') +
          ' ' +
          doc.description +
          ' - ' +
          doc.url,
      )
      .join('\n');
  }

  mapSourcesCopy(sources: SourceResponse[]): string {
    return sources.map(s => s.description + ' - ' + s.url).join('\n') + '\n';
  }

  openPanelSources(): void {
    this.searchPanelDrawer.close();
    this.sourcesDrawer.open(this.options, this.rightWidth);
  }

  closePanelSources(): void {
    this.drawer.close();
  }

  startedOnBoarding(): void {
    effect(() => {
      const lengthContent: string[] = [];
      if (this.isPublicationsHasContent()) lengthContent.push('publications');
      if (this.isOperationalsHasContent()) lengthContent.push('operationals');
      if (lengthContent.length > 0 && !this.openSearchService.spinnerConversational()) {
        setTimeout(() => this.startOnboarding(), 200);
      }
    });
  }

  startOnboarding(reset = false): void {
    if (reset) this.onBoardingService.resetTour(this.onboardingKey);
    void this.onBoardingService.startTour(StepOpenSearchResponseView, this.onboardingKey);
  }

  toggleThumb(type: 'up' | 'down', questionId: string): void {
    if (!questionId) return;
    const current = this.thumbStates()[questionId] ?? null;
    this.thumbStates.update(states => ({
      ...states,
      [questionId]: current === type ? null : type,
    }));
  }

  isQuestionExpandable(question?: string | null): boolean {
    if (!question?.trim()) return false;
    if (question.trim().length < 100) return false;
    const scrollContainer = this.scrollContainerRef?.nativeElement;
    if (!scrollContainer) return question.trim().length > 480;
    const allTexts = Array.from(scrollContainer.querySelectorAll<HTMLElement>('.question-text'));
    const stickyText =
      this.stickyHeaderRef?.nativeElement?.querySelector<HTMLElement>('.question-text');
    const candidates = stickyText ? [stickyText, ...allTexts] : allTexts;
    const el = candidates.find(e => e.textContent?.trim() === question.trim());
    if (!el) return question.trim().length > 480;
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 20;
    const lines = el.scrollHeight / lineHeight;
    return lines > 6;
  }

  isQuestionExpanded(questionKey: string): boolean {
    return !!this.expandedQuestions()[questionKey];
  }

  toggleQuestionExpansion(questionKey: string): void {
    this.expandedQuestions.update(questions => ({
      ...questions,
      [questionKey]: !questions[questionKey],
    }));
  }

  private pinActiveIndex(index: number, durationMs = 2000): void {
    if (this.pinnedActiveTimer !== null) clearTimeout(this.pinnedActiveTimer);
    this.pinnedActiveIndex = index;
    this.pinnedActiveTimer = setTimeout(() => {
      this.pinnedActiveIndex = -1;
      this.pinnedActiveTimer = null;
    }, durationMs);
  }

  private scrollToInteractionAndHighlight(index: number): void {
    const scrollContainer = this.scrollContainerRef?.nativeElement;
    if (!scrollContainer) return;
    const data = this.openSearchService._historyResponse();
    if (!data?.openQueryInteractions) return;
    const interactions = data.openQueryInteractions;
    if (index < 0 || index >= interactions.length) return;
    interactions.forEach((item, i) => {
      item.isActive = i === index;
      item.isSticky = false;
    });
    this.openSearchService._historyResponse.set({ ...data });
    this.currentStickyHeader.set(null);
    this.lastActiveStickyIndex = -1;
    this.pinActiveIndex(index);
    const headers = Array.from(scrollContainer.querySelectorAll('.top-header')) as HTMLElement[];
    const targetHeader = headers[index];
    if (!targetHeader) {
      if (index === interactions.length - 1)
        scrollContainer.scrollTo({ top: scrollContainer.scrollHeight, behavior: 'smooth' });
      return;
    }
    const containerTop = scrollContainer.getBoundingClientRect().top;
    const headerTop = targetHeader.getBoundingClientRect().top;
    const targetScrollTop = scrollContainer.scrollTop + (headerTop - containerTop) - 16;
    scrollContainer.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
  }

  private applyHighlightOnly(element: HTMLElement, query: string): void {
    if (!query || !element) return;
    const lowerQuery = query.toLowerCase();
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    const textNodes: Text[] = [];
    let node: Node | null;
    while ((node = walker.nextNode())) textNodes.push(node as Text);

    textNodes.forEach(textNode => {
      const text = textNode.textContent ?? '';
      const idx = text.toLowerCase().indexOf(lowerQuery);
      if (idx === -1) return;

      const fragment = document.createDocumentFragment();
      if (idx > 0) fragment.appendChild(document.createTextNode(text.slice(0, idx)));
      const mark = document.createElement('mark');
      mark.className = 'text-highlight';
      mark.setAttribute('data-search-highlight', 'true');
      mark.textContent = text.slice(idx, idx + query.length);
      fragment.appendChild(mark);
      const rest = text.slice(idx + query.length);
      if (rest) fragment.appendChild(document.createTextNode(rest));
      textNode.parentNode?.replaceChild(fragment, textNode);
    });
  }

  private setupStickyHeaderDetection(): void {
    setTimeout(() => {
      const scrollContainer = this.scrollContainerRef?.nativeElement;
      if (!scrollContainer) return;
      this.scrollListener = () => this.detectActiveHeader(scrollContainer);
      scrollContainer.addEventListener('scroll', this.scrollListener, { passive: true });
      this.detectActiveHeader(scrollContainer);
    }, 10);
  }

  private detectActiveHeader(scrollContainer: HTMLElement): void {
    const headers = Array.from(scrollContainer.querySelectorAll<HTMLElement>('.top-header'));
    if (!headers.length) {
      if (this.lastActiveStickyIndex !== -1) {
        this.lastActiveStickyIndex = -1;
        this.currentStickyHeader.set(null);
      }
      return;
    }
    const data = this.historyResponse();
    if (!data?.openQueryInteractions?.length) return;
    const containerTop = scrollContainer.getBoundingClientRect().top;

    if (this.pinnedActiveIndex >= 0) {
      if (this.lastActiveStickyIndex !== -1) {
        this.lastActiveStickyIndex = -1;
        data.openQueryInteractions.forEach((el, i) => {
          el.isSticky = false;
          el.isActive = i === this.pinnedActiveIndex;
        });
        this.openSearchService._historyResponse.set({ ...data });
        this.currentStickyHeader.set(null);
      }
      return;
    }

    const isAtBottom =
      scrollContainer.scrollTop + scrollContainer.clientHeight >= scrollContainer.scrollHeight - 4;
    if (isAtBottom && data.openQueryInteractions.length > 0) {
      const lastIndex = data.openQueryInteractions.length - 1;
      const lastHeader = headers[lastIndex];
      if (lastHeader) {
        const lastRect = lastHeader.getBoundingClientRect();
        if (lastRect.bottom <= containerTop) {
          if (this.lastActiveStickyIndex !== lastIndex) {
            this.lastActiveStickyIndex = lastIndex;
            data.openQueryInteractions.forEach((el, i) => {
              el.isActive = i === lastIndex;
              el.isSticky = i === lastIndex;
            });
            this.openSearchService._historyResponse.set({ ...data });
            this.currentStickyHeader.set({
              answer: data.openQueryInteractions[lastIndex].question,
              index: lastIndex,
            });
          }
          return;
        }
      }
      const sentinel = -(lastIndex + 1000);
      if (this.lastActiveStickyIndex !== sentinel) {
        this.lastActiveStickyIndex = sentinel;
        data.openQueryInteractions.forEach((el, i) => {
          el.isSticky = false;
          el.isActive = i === lastIndex;
        });
        this.openSearchService._historyResponse.set({ ...data });
        this.currentStickyHeader.set(null);
      }
      return;
    }
    let activeHeaderIndex = -1;
    for (let i = 0; i < headers.length; i++) {
      if (headers[i].getBoundingClientRect().top < containerTop + 10) activeHeaderIndex = i;
      else break;
    }
    if (activeHeaderIndex === -1) {
      if (this.lastActiveStickyIndex !== -1) {
        this.lastActiveStickyIndex = -1;
        data.openQueryInteractions.forEach(el => {
          el.isSticky = false;
          el.isActive = false;
        });
        this.openSearchService._historyResponse.set({ ...data });
        this.currentStickyHeader.set(null);
      }
      return;
    }
    const activeHeader = headers[activeHeaderIndex];
    const activeHeaderRect = activeHeader.getBoundingClientRect();
    const headerIndex = parseInt(activeHeader.getAttribute('data-header-index') || '0', 10);
    const nextHeader = headers[activeHeaderIndex + 1];
    const nextHeaderTop = nextHeader ? nextHeader.getBoundingClientRect().top : Infinity;
    const stickyHeaderHeight = this.stickyHeaderRef?.nativeElement?.offsetHeight ?? 0;
    const showSticky =
      activeHeaderRect.bottom <= containerTop && nextHeaderTop > containerTop + stickyHeaderHeight;
    if (showSticky) {
      if (this.lastActiveStickyIndex !== headerIndex) {
        this.lastActiveStickyIndex = headerIndex;
        if (headerIndex >= 0 && headerIndex < data.openQueryInteractions.length) {
          data.openQueryInteractions.forEach((el, i) => {
            el.isActive = i === headerIndex;
            el.isSticky = i === headerIndex;
          });
          this.openSearchService._historyResponse.set({ ...data });
          this.currentStickyHeader.set({
            answer: data.openQueryInteractions[headerIndex].question,
            index: headerIndex,
          });
        }
      }
    } else {
      if (this.lastActiveStickyIndex !== -1) {
        this.lastActiveStickyIndex = -1;
        data.openQueryInteractions.forEach(el => {
          el.isSticky = false;
        });
        this.openSearchService._historyResponse.set({ ...data });
        this.currentStickyHeader.set(null);
      }
    }
  }

  private effectAutoOpenDrawerOnResponse(): void {
    effect(() => {
      const history = this.historyResponse()?.openQueryInteractions || [];
      localStorage.setItem('responseView', 'true');
      if (!history.length) return;

      const last = history[history.length - 1];
      if (!last.response) return;
      if (!this.hasSourcesForInteraction(last)) return;
      if (!this.options.length) return;
      if (this.lastAutoOpenedAnswerId === last.question_id) return;
      this.lastAutoOpenedAnswerId = last.question_id ?? null;
      if (this.isCuratedInteraction(last) && (last.sources?.length ?? 0) === 0) {
        this.sourcesDrawer.open(this.options, this.rightWidth);
      }
    });
  }
}
